<template>
  <div class="admin-app-layout">
    <TopBar
      :sidebar-open="sidebarOpen"
      :houses="nav.platformLevel ? [] : houses"
      :house="house"
      @toggle-sidebar="toggleSidebar"
      @select-house="(code) => emit('select-house', code)"
    >
      <template v-if="$slots.logo" #logo><slot name="logo" /></template>
    </TopBar>

    <div class="admin-app-layout__body">
      <ShellSidebar
        :nav="nav"
        :contexts="contexts"
        :active-page="activePage"
        :open="sidebarOpen"
        @navigate="(id) => emit('navigate', id)"
        @select-context="(id) => emit('select-context', id)"
      />

      <main class="admin-app-layout__main">
        <PageHeader
          :nav="nav"
          :active-page="activePage"
          :sub-crumbs="subCrumbs"
          @navigate="(id) => emit('navigate', id)"
        />
        <div class="admin-app-layout__content" :style="{ padding: contentPadding }">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import TopBar from './TopBar.vue'
import ShellSidebar from './ShellSidebar.vue'
import PageHeader from './PageHeader.vue'
import { provideBreadcrumb } from './composables/useBreadcrumb.js'

// Purely presentational shell: top bar + collapsible sidebar (context switcher
// + nav) + auto-breadcrumb header. Pages render into the default slot. The host
// owns routing and state; this component only emits intents.
//
// The account side of the top bar is not this shell's: the avatar there is the
// shared App Menu shell's (@hds/common-ui-lib), which owns who is signed in,
// the theme, the language and Log Out.
//
// Two levels above a page: a CONTEXT (Platform Administration, Auction
// Operations — the design's "App", renamed because that word is the webapp)
// holds sections, and a section holds pages. `nav` is the current context's
// tree; `contexts` is every context the switcher may offer.
//
// A page that needs the whole viewport (the clerk console) does not render
// inside this shell at all — the host mounts it bare in its own tab.
//
// Trimmed from vibe-portal's copy. `user`, `sign-out` and the theme control
// went to the shared App Menu shell, and `showDashboard` went because this app
// registers no dashboard route. `houses` is back: a context that is not platform-level
// stands inside one house. `nav` does not default either, because a shell that
// renders somebody else's menu when the host forgets one hides the mistake.
defineProps({
  nav: { type: Object, required: true },
  contexts: { type: Array, default: () => [] },
  activePage: { type: String, required: true },
  // `[{ code, name }]`; shown only when `nav.platformLevel` is false.
  houses: { type: Array, default: () => [] },
  house: { type: String, default: '' },
  // Padding applied to the scrolling content area. Pass '0' for full-bleed pages.
  contentPadding: { type: String, default: '24px' },
})
const emit = defineEmits(['navigate', 'select-context', 'select-house'])

// Breadcrumb: layout owns the subCrumbs that pages publish via useBreadcrumb().
const { subCrumbs } = provideBreadcrumb()

// Sidebar open/closed, persisted, default open. `administration-` prefixes
// what this shell stores — vibe-portal's `aa-` belongs to vibe-portal.
const SIDEBAR_KEY = 'administration-sidebar'
const sidebarOpen = ref(localStorage.getItem(SIDEBAR_KEY) !== 'false')
function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
  localStorage.setItem(SIDEBAR_KEY, String(sidebarOpen.value))
}
</script>

<style scoped>
.admin-app-layout {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--surface-ground);
  color: var(--p-text-color);
  font-family: var(--p-font-body);
  font-size: 14px;
  overflow: hidden;
}

.admin-app-layout__body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.admin-app-layout__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.admin-app-layout__content {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

</style>
